import { useState } from "react";

import {
  Badge,
  Button,
  ButtonOwnProps,
  Chip,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BlockIcon from "@mui/icons-material/Block";
import PendingIcon from "@mui/icons-material/Pending";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";

import ErrorChip from "../utils/ErrorChip";
import SuccessChip from "../utils/SuccessChip";
import { Organization } from "../../hooks/confg/base";
import { ROLES, STATUS, User } from "../auth/AuthProvider";
import {
  EditAccount,
  EditProvider,
  EditRole,
  RemoveAccount,
} from "./ManageUserComponents";

type ManageViewProps = {
  user: User;
  open: boolean;
  role: ROLES | "";
  status: STATUS | "";
  organization: Organization;
  onClose: () => void;
};

const ManageView = (props: ManageViewProps) => {
  const { open, user, role, status, organization, onClose } = props;

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
      <Stack direction={"row"} justifyContent={"space-between"}>
        <DialogTitle>
          <Chip
            color="success"
            icon={<AssignmentIndIcon />}
            label={
              <>
                <b>{organization.name}</b>: {user.email.toUpperCase()}
              </>
            }
          />
        </DialogTitle>

        <Button size="small" color="inherit" onClick={onClose}>
          <CloseIcon />
        </Button>
      </Stack>

      <Divider />

      <DialogContent>
        <Typography gutterBottom variant="h6" component="div">
          Role
        </Typography>

        <DialogContentText variant="caption">
          To manage, click Edit then select new user role. Changes will not take
          effect until submitted & confirmed. Note: For security, cannot modify
          or change to 'admin' role.
        </DialogContentText>

        <EditRole user={user} current={role} organization={organization} />

        <Divider sx={{ mb: 2 }} />

        <Typography gutterBottom variant="h6" component="div">
          IDP
        </Typography>

        <DialogContentText variant="caption">
          To modify this account's identity provider, please select new IDP(s)
          then submit. Changes will not take effect until submitted & confirmed.
          Note: Will ask for confirmation.
        </DialogContentText>

        <EditProvider user={user} />

        <Divider sx={{ mb: 2 }} />

        <Stack direction={"row"} justifyContent={"space-between"}>
          <Typography gutterBottom variant="h6" component="div">
            Account
          </Typography>

          {status === "active" ? (
            <SuccessChip
              info={"This account is currently active"}
              label={"active"}
              placement="left"
            />
          ) : (
            <ErrorChip
              info={"This account is currently: " + status.toUpperCase()}
              icon={
                status === "pending" ? (
                  <PendingIcon />
                ) : status === "invited" ? (
                  <InsertInvitationIcon />
                ) : (
                  <BlockIcon />
                )
              }
              label={status}
              color={
                status === "pending" || status === "invited"
                  ? "warning"
                  : "error"
              }
              placement="left"
            />
          )}
        </Stack>

        <DialogContentText variant="caption">
          To modify account access, select new status then submit. Changes will
          not take effect until submitted & confirmed. Note: Will ask for
          confirmation.
        </DialogContentText>

        <EditAccount user={user} current={status} organization={organization} />

        <RemoveAccount
          user={user}
          organization={organization}
          sx={{ mt: 0.5 }}
        />
      </DialogContent>
    </Dialog>
  );
};

const ManageUser = ({
  user,
  organization,
  size = "medium",
  iconOnly = false,
  disabled = false,
  fullWidth = false,
}: {
  user: User;
  organization: Organization;
  size?: ButtonOwnProps["size"];
  detail?: any;
  iconOnly?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
}) => {
  const org = user.organizations.find((org) => org.id === organization.id);
  const role = org ? org.role : "";
  const status = org ? org.status : "";

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const Manage = iconOnly ? (
    <IconButton size={size} disabled={disabled}>
      <ManageAccountsIcon
        type="submit"
        color={disabled ? "disabled" : "inherit"}
        onClick={handleOpen}
      />
    </IconButton>
  ) : (
    <Button
      size={size}
      type="submit"
      color="primary"
      variant="contained"
      onClick={handleOpen}
      disabled={disabled}
      fullWidth={fullWidth}
    >
      Manage
    </Button>
  );

  return (
    <div>
      {status === "pending" ? (
        <Tooltip title="Pending account activation" placement="left">
          <Badge color="error" variant="dot">
            {Manage}
          </Badge>
        </Tooltip>
      ) : (
        Manage
      )}

      <ManageView
        open={open}
        user={user}
        role={role}
        status={status}
        organization={organization}
        onClose={handleClose}
      />
    </div>
  );
};

export default ManageUser;
